import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const LEVELS = ['A1', 'A2', 'B1', 'B2'];
const CATS   = ['lesson', 'exam_tips', 'vocabulary', 'grammar'];
const LABELS = { lesson: 'Lessons', exam_tips: 'Exam Tips', vocabulary: 'Vocabulary', grammar: 'Grammar' };
const ICONS  = { lesson: '🎓', exam_tips: '📋', vocabulary: '🔤', grammar: '📐' };

// ── Level tab component ───────────────────────────────────────
function LevelTabs({ active, onChange }) {
  return (
    <div className="flex gap-0 rounded-xl overflow-hidden border border-gray-200 w-fit shadow-sm">
      {LEVELS.map((lv, i) => (
        <button
          key={lv}
          onClick={() => onChange(lv)}
          className="relative px-5 py-2.5 text-sm font-bold transition-all duration-200 focus:outline-none"
          style={{
            background:   active === lv ? '#DD0000' : '#FFFFFF',
            color:        active === lv ? '#FFFFFF' : '#1A1A1A',
            borderRight:  i < LEVELS.length - 1 ? '1px solid #E0E0E0' : 'none',
          }}
          onMouseEnter={e => { if (active !== lv) e.currentTarget.style.background = '#FFF0F0'; }}
          onMouseLeave={e => { if (active !== lv) e.currentTarget.style.background = '#FFFFFF'; }}
        >
          {active === lv && (
            <span
              className="absolute bottom-0 left-0 right-0 h-0.5"
              style={{ background: '#FFCE00' }}
            />
          )}
          {lv}
        </button>
      ))}
    </div>
  );
}

export default function StudentVideosPage() {
  const { user }      = useAuth();
  const { addToast }  = useToast();

  const currentLevel  = user?.student?.currentLevel || user?.currentLevel || 'A1';
  const unlockedLevels = user?.student?.unlockedLevels || user?.unlockedLevels || ['A1'];

  const [videos, setVideos]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeLevel, setActiveLevel] = useState(currentLevel);
  const [activeCat, setActiveCat] = useState('all');
  const [playing, setPlaying]     = useState(null); // youtube_id

  useEffect(() => {
    API.get('/videos')
      .then(({ data }) => setVideos(data.videos || []))
      .catch(() => addToast('Failed to load videos', 'error'))
      .finally(() => setLoading(false));
  }, []);

  // Client-side filter: level + category
  const filtered = videos.filter(v => {
    const levelMatch = v.level === activeLevel;
    const catMatch   = activeCat === 'all' || v.category === activeCat;
    return levelMatch && catMatch;
  });

  // Check if a level is locked
  const isLocked = lv => !unlockedLevels.includes(lv);

  const handleLevelChange = (lv) => {
    setActiveLevel(lv);
    setActiveCat('all');
    setPlaying(null);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <SectionHeader
          title="Video Library"
          subtitle="Watch German lesson videos for your level"
        />
        <LevelTabs active={activeLevel} onChange={handleLevelChange} />
      </div>

      {/* Locked level notice */}
      {isLocked(activeLevel) && (
        <div
          className="mb-5 p-3 rounded-xl border flex items-center gap-3"
          style={{ background: '#FFF8E1', borderColor: '#FFCE00' }}
        >
          <span className="text-xl">🔒</span>
          <p className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>
            Level {activeLevel} is locked. Complete your current level to unlock.
          </p>
        </div>
      )}

      {/* Category tabs */}
      <div
        className="flex gap-1 p-1 rounded-xl mb-6 overflow-x-auto"
        style={{ background: '#F5F5F5' }}
      >
        {['all', ...CATS].map(tab => (
          <button
            key={tab}
            onClick={() => { setActiveCat(tab); setPlaying(null); }}
            className="px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap"
            style={{
              background: activeCat === tab ? '#FFFFFF' : 'transparent',
              color:      activeCat === tab ? '#DD0000' : '#555555',
              boxShadow:  activeCat === tab ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            {tab === 'all' ? '🎬 All' : `${ICONS[tab]} ${LABELS[tab]}`}
          </button>
        ))}
      </div>

      {/* Embedded player */}
      {playing && (
        <div className="mb-8">
          <div
            className="relative w-full rounded-2xl overflow-hidden shadow-xl border border-gray-200"
            style={{ paddingBottom: '56.25%', height: 0, background: '#000' }}
          >
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src={`https://www.youtube.com/embed/${playing}?autoplay=1&rel=0`}
              title="Video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
          <button
            onClick={() => setPlaying(null)}
            className="mt-3 text-xs font-medium transition-colors"
            style={{ color: '#888' }}
          >
            × Close player
          </button>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner size="lg" className="text-brand-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-5xl mb-4">🎬</p>
          <p className="text-base font-bold mb-1" style={{ color: '#1A1A1A' }}>
            No videos for Level {activeLevel}
            {activeCat !== 'all' ? ` — ${LABELS[activeCat]}` : ''}
          </p>
          <p className="text-sm" style={{ color: '#888' }}>
            Your teacher hasn't added any videos here yet.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(v => (
            <button
              key={v.id}
              onClick={() => setPlaying(v.youtube_id)}
              className="text-left rounded-xl overflow-hidden border border-gray-200 bg-white group transition-all"
              style={{ boxShadow: 'none' }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(221,0,0,0.12)';
                e.currentTarget.style.borderColor = '#DD0000';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = '#E5E7EB';
              }}
            >
              {/* Thumbnail */}
              <div
                className="relative overflow-hidden"
                style={{ paddingBottom: '56.25%', background: '#111' }}
              >
                <img
                  src={v.thumbnail_url || `https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`}
                  alt={v.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={e => {
                    e.target.src = `https://img.youtube.com/vi/${v.youtube_id}/hqdefault.jpg`;
                  }}
                />
                {/* Play overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform"
                    style={{ background: 'rgba(221,0,0,0.88)' }}
                  >
                    <span className="text-white text-lg ml-0.5">▶</span>
                  </div>
                </div>
                {/* Currently playing */}
                {playing === v.youtube_id && (
                  <div
                    className="absolute top-2 right-2 px-2 py-0.5 rounded text-xs font-bold text-white"
                    style={{ background: '#DD0000' }}
                  >
                    ▶ Playing
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-3">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-xs" style={{ color: '#888' }}>
                    {ICONS[v.category]} {LABELS[v.category]}
                  </span>
                </div>
                <h3
                  className="text-sm font-bold line-clamp-2 leading-snug"
                  style={{ color: '#1A1A1A' }}
                >
                  {v.title}
                </h3>
                {v.description && (
                  <p className="text-xs mt-1 line-clamp-2" style={{ color: '#888' }}>
                    {v.description}
                  </p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
